'use client';

import React, { useState, useEffect, useMemo, memo, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { usePerformanceLabsIndia } from './PerformanceLabsIndiaContext';
import PlanCard from './PlanCard';
import ComparePlans from './ComparePlans';
import CategorySidebar from './CategorySidebar';
import { isMarketingBannerActive } from 'components/shared/Header/MarketingBanner';
import { useGlobalUI } from 'contexts/GlobalUIContext';
import { PerformanceLabSection, PerformanceLabPlan } from './types';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import styles from './IndiaDynamicContent.module.css';

const resolveCategoryFromQuery = (
  category: string | string[] | undefined,
  sections: PerformanceLabSection[],
) => {
  const categoryId = Array.isArray(category) ? category[0] : category;
  if (categoryId && sections.some((section) => section.id === categoryId)) {
    return categoryId;
  }

  return sections[0]?.id ?? '';
};

const resolveCategoryFromPlanId = (
  planId: string | undefined,
  sections: PerformanceLabSection[],
) => {
  if (!planId) return undefined;

  for (const section of sections) {
    if (section.plans.some((plan) => plan.id === planId)) {
      return section.id;
    }

    for (const subcategory of section.subcategories ?? []) {
      if (subcategory.plans.some((plan) => plan.id === planId)) {
        return section.id;
      }
    }
  }

  return undefined;
};

const resolveActiveCategory = (
  category: string | string[] | undefined,
  plan: string | string[] | undefined,
  sections: PerformanceLabSection[],
) => {
  const planId = Array.isArray(plan) ? plan[0] : plan;
  const planCategory = resolveCategoryFromPlanId(planId, sections);

  if (planCategory) {
    return planCategory;
  }

  return resolveCategoryFromQuery(category, sections);
};

const IndiaDynamicContent: React.FC = memo(() => {
  const { sections, error } = usePerformanceLabsIndia();
  const t = useTranslations('diagnostics');
  const searchParams = useSearchParams();

  const GlobalUI = useGlobalUI();
  const [isMobile, setIsMobile] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>(() =>
    resolveCategoryFromQuery(
      searchParams?.get('category') ?? undefined,
      sections,
    ),
  );
  const [activeTab, setActiveTab] = useState<'plans' | 'compare'>('plans');
  const [activeSubcategory, setActiveSubcategory] = useState<string | null>(
    null,
  );

  const sidebarRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const categoryRefs = useRef<Record<string, HTMLElement | null>>({});
  const prevCategoryRef = useRef(activeCategory);

  const hasMarketingBanner = isMarketingBannerActive();
  const marketingBannerHeight = hasMarketingBanner ? 32 : 0;
  const sidebarTopOffset =
    (GlobalUI.headerHeight ?? 80) + marketingBannerHeight;

  const categoryMobileTopOffset = GlobalUI.headerVisible
    ? GlobalUI.headerHeight ?? 80
    : 0;

  const compareStickyTopOffset = GlobalUI.headerVisible
    ? GlobalUI.headerHeight ?? 80
    : 0;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!searchParams) return;

    const nextCategory = resolveActiveCategory(
      searchParams.get('category') ?? undefined,
      searchParams.get('plan') ?? undefined,
      sections,
    );

    setActiveCategory((prev) => (prev === nextCategory ? prev : nextCategory));
  }, [searchParams, sections]);

  useEffect(() => {
    if (!searchParams) return;

    const planId = searchParams.get('plan') ?? undefined;

    if (!planId || activeTab !== 'plans') return;

    const scrollToPlan = () => {
      const planElement = document.querySelector(`[data-plan-id="${planId}"]`);
      planElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(scrollToPlan);
    });
  }, [searchParams, activeCategory, activeTab]);

  useEffect(() => {
    if (!isMobile) return;

    const sidebarElement = sidebarRef.current;
    const activeCategoryElement = categoryRefs.current[activeCategory];

    if (sidebarElement && activeCategoryElement) {
      const sidebarWidth = sidebarElement.clientWidth;
      const categoryLeft = activeCategoryElement.offsetLeft;
      const categoryWidth = activeCategoryElement.clientWidth;

      const scrollLeft = categoryLeft - sidebarWidth / 2 + categoryWidth / 2;
      sidebarElement.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    }
  }, [activeCategory, isMobile]);

  useEffect(() => {
    const categoryChanged = prevCategoryRef.current !== activeCategory;
    prevCategoryRef.current = activeCategory;

    if (!categoryChanged) return;

    if (isMobile && contentRef.current) {
      const headerHeight = GlobalUI.headerVisible
        ? GlobalUI.headerHeight ?? 80
        : 0;
      const sidebarHeight = sidebarRef.current?.clientHeight ?? 60; // fallback height

      const elementTop =
        contentRef.current.getBoundingClientRect().top + window.pageYOffset;
      const targetPosition = elementTop - headerHeight - sidebarHeight - 16; // 16px buffer

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth',
      });
    } else {
      contentRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }

    setActiveTab('plans');
    setActiveSubcategory(null);
  }, [activeCategory, isMobile]);
  const activeSection = useMemo(
    () => sections.find((s) => s.id === activeCategory),
    [sections, activeCategory],
  );

  const hasSubcategories = !!(
    activeSection?.subcategories && activeSection.subcategories.length > 0
  );

  const displayedPlans: PerformanceLabPlan[] = useMemo(() => {
    if (!activeSection) return [];
    if (hasSubcategories) {
      const subcats = activeSection.subcategories ?? [];
      const activeSub = activeSubcategory || subcats[0]?.id;
      const found = subcats.find((sc) => sc.id === activeSub);
      return found ? found.plans : subcats[0]?.plans || [];
    }
    return activeSection.plans;
  }, [activeSection, hasSubcategories, activeSubcategory]);

  const hasCompareData = !!(
    activeSection?.compareData && activeSection?.compareHeaders
  );

  if (error) {
    return <div className={styles.errorContainer}>{error}</div>;
  }

  return (
    <div className={styles.contentLayout} ref={contentRef}>
      <CategorySidebar
        sections={sections}
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
        topOffset={sidebarTopOffset}
        mobileTopOffset={categoryMobileTopOffset}
        sidebarRef={sidebarRef}
        categoryRefs={categoryRefs}
      />

      <main className={styles.mainContent}>
        {activeSection && (
          <section
            className={styles.sectionContainer}
            key={activeSection.id}
            id={activeSection.id}
          >
            <div className={styles.heroBanner}>
              {activeSection.heroImageUrl && (
                <CustomImage
                  className={styles.heroImage}
                  src={activeSection.heroImageUrl}
                  alt={t(activeSection.heroTitle)}
                  fill
                />
              )}
              <div
                className={styles.heroGradientOverlay}
                data-position={activeSection.heroTextPosition || 'left'}
              />
              {activeSection.heroDescription && (
                <div className={styles.heroTextContainer}>
                  <div className={styles.heroDot} />
                  <p className={styles.heroDescription}>
                    {t(activeSection.heroDescription)}
                  </p>
                </div>
              )}
            </div>

            {hasCompareData && (
              <div className={styles.desktopOnly}>
                <div className={styles.tabBar}>
                  <button
                    className={styles.tabItem}
                    data-active={activeTab === 'plans' ? 'true' : undefined}
                    onClick={() => setActiveTab('plans')}
                  >
                    {activeSection?.plansTabLabel
                      ? t(activeSection.plansTabLabel)
                      : `${t(
                          activeSection?.sidebarLabel ?? activeCategory,
                        )} ${t('ui.plans')}`}
                  </button>
                  <button
                    className={styles.tabItem}
                    data-active={activeTab === 'compare' ? 'true' : undefined}
                    onClick={() => setActiveTab('compare')}
                  >
                    {t('ui.comparePlans')}
                  </button>
                </div>
              </div>
            )}

            {hasSubcategories && activeTab === 'plans' && (
              <div className={styles.desktopOnly}>
                <div className={styles.tabBar}>
                  {(activeSection.subcategories ?? []).map((sc) => {
                    const isActive =
                      (activeSubcategory ||
                        activeSection.subcategories?.[0]?.id) === sc.id;
                    return (
                      <button
                        key={sc.id}
                        className={styles.tabItem}
                        data-active={isActive ? 'true' : undefined}
                        onClick={() => setActiveSubcategory(sc.id)}
                      >
                        {t(sc.label)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {(activeTab === 'plans' || !hasCompareData || isMobile) &&
              (hasSubcategories ? (
                <>
                  <div className={styles.desktopOnly}>
                    <div
                      className={styles.plansGrid}
                      data-layout={activeSection.cardLayout}
                      data-compact={
                        activeSection.cardVariant === 'compact' ||
                        activeSection.cardVariant === 'service'
                          ? 'true'
                          : undefined
                      }
                    >
                      {displayedPlans.map((plan) => (
                        <div
                          className={styles.planCardAnchor}
                          key={plan.id}
                          data-plan-id={plan.id}
                        >
                          <PlanCard
                            plan={plan}
                            variant={activeSection.cardVariant || 'default'}
                            sectionId={activeCategory}
                            compareData={activeSection.compareData}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className={styles.mobileOnly}>
                    {(activeSection.subcategories ?? []).map((sc) => (
                      <React.Fragment key={sc.id}>
                        <h3 className={styles.subcategoryHeader}>
                          {t(sc.label)}
                        </h3>
                        <div
                          className={styles.plansGrid}
                          data-layout={activeSection.cardLayout}
                          data-compact={
                            activeSection.cardVariant === 'compact' ||
                            activeSection.cardVariant === 'service'
                              ? 'true'
                              : undefined
                          }
                        >
                          {sc.plans.map((plan) => (
                            <div
                              className={styles.planCardAnchor}
                              key={plan.id}
                              data-plan-id={plan.id}
                            >
                              <PlanCard
                                plan={plan}
                                variant={activeSection.cardVariant || 'default'}
                                sectionId={activeCategory}
                                compareData={activeSection.compareData}
                              />
                            </div>
                          ))}
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </>
              ) : (
                <div
                  className={styles.plansGrid}
                  data-layout={activeSection.cardLayout}
                  data-compact={
                    activeSection.cardVariant === 'compact' ||
                    activeSection.cardVariant === 'service'
                      ? 'true'
                      : undefined
                  }
                >
                  {displayedPlans.map((plan) => (
                    <div
                      className={styles.planCardAnchor}
                      key={plan.id}
                      data-plan-id={plan.id}
                    >
                      <PlanCard
                        plan={plan}
                        variant={activeSection.cardVariant || 'default'}
                        sectionId={activeCategory}
                        compareData={activeSection.compareData}
                      />
                    </div>
                  ))}
                </div>
              ))}

            {hasCompareData && (
              <>
                <div className={styles.desktopOnly}>
                  {activeTab === 'compare' && (
                    <ComparePlans
                      headers={activeSection.compareHeaders ?? []}
                      categories={activeSection.compareData ?? []}
                      stickyTopOffset={compareStickyTopOffset}
                    />
                  )}
                </div>
                <div className={styles.mobileOnly}>
                  <ComparePlans
                    headers={activeSection.compareHeaders ?? []}
                    categories={activeSection.compareData ?? []}
                    stickyTopOffset={compareStickyTopOffset}
                  />
                </div>
              </>
            )}
          </section>
        )}
      </main>
    </div>
  );
});

IndiaDynamicContent.displayName = 'IndiaDynamicContent';

export default IndiaDynamicContent;
